import { Helmet } from 'react-helmet-async'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAnalytics, faPlus } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule, PageHeader,
} from '@domain-group/suit-theme'

import { useNumericParams } from '@domain-group/react-hooks'

import useCampaignSelector from '@hooks/useCampaignSelector'
import useTrackingLink, { generateUrls } from '@hooks/useTrackingLink'
import useTrackingLinks from '@hooks/useTrackingLinks'

import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'

import defaultRequestOptions from '@sections/Client/packs/Campaign/defaultRequestOptions'

import TrackingLinkListItem from '../TrackingLinkListItem'
import TrackingLinksBlankState from '../TrackingLinksBlankState'

const PageContent = () => {
  const { campaignId } = useNumericParams()

  const trackingLinksPayload = useTrackingLinks({
    filters: {
      subject_id: campaignId,
      subject_type: 'Campaign',
    },
    performHttpRequests: !!campaignId,
    requestOptions: defaultRequestOptions.trackingLink,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filteredTrackingLinks,
    filteredTrackingLinksCount,
    hasFilteredTrackingLinks,
    loading,
  } = trackingLinksPayload

  const { campaign, campaign: { address } } = useCampaignSelector()

  const initTrackingLink = {
    owner_id: campaign.agency_id,
    owner_type: 'Agency',
    subject_id: campaignId,
    subject_type: 'Campaign',
  }

  const trackingLinkPayload = useTrackingLink(initTrackingLink)
  const {
    callbacks: { createOrEditTrackingLink },
  } = trackingLinkPayload

  const { trackingLinksIndexUrl } = generateUrls({ prefixRoute: `campaigns/${campaignId}/edit` })

  return (
    <>
      <Helmet>
        <title>{address || 'Campaign'} | Tracking Links</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button
            buttonStyle="primaryCreate"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => createOrEditTrackingLink()}
            size="medium"
          >
            Create Tracking Link
          </Button>
        )}
        activeTabBarItemKey="dashboard"
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        nestedNavigation
        tabBarItems={[
          {
            href: trackingLinksIndexUrl,
            icon: faAnalytics,
            key: 'dashboard',
            title: 'Dashboard',
          },
        ]}
        title="Tracking Links"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.campaign.trackingLinks.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredTrackingLinks && <TrackingLinksBlankState />}

            {!loading && hasFilteredTrackingLinks && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Tracking Links">
                {/*
                 // @ts-expect-error TS2322 */}
                <DashboardModule.ScrollableSection maxHeight={600}>
                  {filteredTrackingLinks.map(trackingLink => (
                    <TrackingLinkListItem key={trackingLink.id} trackingLink={trackingLink} />
                  ))}
                </DashboardModule.ScrollableSection>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredTrackingLinksCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.campaign.trackingLinks.sidebar" />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
